/**
 * BrokenReferenceChip — a visible, hoverable placeholder for a value
 * reference the backend value-guard could NOT resolve to a tool result.
 *
 * Why this exists: the AI sometimes emits a `[[VALUE:id]]` marker whose
 * id doesn't match any tool call this turn (a hallucinated id, an id
 * dredged from a prior turn that's no longer in scope, or more markers
 * than tool calls in a comparison table). Previously these collapsed to
 * a bare "[?]" — a silent failure the user couldn't diagnose. Now every
 * unresolved reference stays present as a distinct grey dashed chip with
 * an ⓘ icon; hovering reveals what the AI was trying to cite and why we
 * couldn't honour it. This makes the broken state auditable.
 *
 * The chip is deliberately NOT the orange "verified" style and NOT the
 * old yellow "unverified" style — it's a neutral "trace" affordance.
 *
 * @param {object} props
 * @param {object} props.trace — { id, context, reason } decoded from the
 *   backend's [[BROKENREF:…]] marker.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'preact/hooks'
import { createPortal } from 'preact/compat'

const TOOLTIP_GAP = 6
const TOOLTIP_VIEWPORT_PAD = 8

const REASON_TEXT = {
  'no-tool-result': 'No matching tool result this turn — the AI may have referenced a value it never fetched, or reused an id from an earlier message.',
  'unknown-id': 'The reference id does not match any tool call in this conversation.',
  'unknown-ref': 'This value reference does not match any fetched value in this conversation.',
  'undecodable-trace': 'The reference could not be decoded.',
  unresolved: 'This reference could not be matched to a verified value.',
  unknown: 'This reference could not be matched to a verified value.',
}

export function BrokenReferenceChip({ trace }) {
  const t = trace || {}
  const tooltipId = useId()
  const [open, setOpen] = useState(false)
  const chipRef = useRef(null)
  const tooltipRef = useRef(null)
  const [tipStyle, setTipStyle] = useState({ top: 0, left: 0, visibility: 'hidden' })

  const reasonText = REASON_TEXT[t.reason] || REASON_TEXT.unresolved

  useLayoutEffect(() => {
    if (!open) return
    const chip = chipRef.current
    const tip = tooltipRef.current
    if (!chip || !tip) return

    const chipRect = chip.getBoundingClientRect()
    const tipRect = tip.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    let top = chipRect.top - tipRect.height - TOOLTIP_GAP
    let left = chipRect.left + chipRect.width / 2 - tipRect.width / 2

    if (top < TOOLTIP_VIEWPORT_PAD) {
      top = chipRect.bottom + TOOLTIP_GAP
    }
    if (top + tipRect.height > vh - TOOLTIP_VIEWPORT_PAD) {
      top = Math.max(TOOLTIP_VIEWPORT_PAD, vh - tipRect.height - TOOLTIP_VIEWPORT_PAD)
    }
    const minLeft = TOOLTIP_VIEWPORT_PAD
    const maxLeft = vw - tipRect.width - TOOLTIP_VIEWPORT_PAD
    if (left < minLeft) left = minLeft
    if (left > maxLeft) left = maxLeft

    setTipStyle({ top, left, visibility: 'visible' })
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const reposition = () => setTipStyle((s) => ({ ...s }))
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open])

  // Short visible label: the context (what the AI was citing) if we have
  // it, else a neutral marker with the id's tail so two broken chips in
  // one message are distinguishable at a glance (full id in the tooltip).
  const idTail = t.id ? ` ·${String(t.id).slice(-4)}` : ''
  const visibleLabel = t.context ? t.context : `unverified reference${idTail}`

  return (
    <span class="ion-broken-chip-wrap">
      <span
        ref={chipRef}
        class="ion-broken-chip"
        role="img"
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
        aria-label={`Unresolved reference: ${visibleLabel}. ${reasonText}`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <span class="ion-broken-chip__icon" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="11" height="11">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.5" />
            <line x1="8" y1="7" x2="8" y2="11.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            <circle cx="8" cy="4.6" r="0.9" fill="currentColor" />
          </svg>
        </span>
        <span class="ion-broken-chip__label">{visibleLabel}</span>
      </span>
      {open && typeof document !== 'undefined' &&
        createPortal(
          <span
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            class="ion-broken-chip__tooltip"
            style={{
              position: 'fixed',
              top: `${tipStyle.top}px`,
              left: `${tipStyle.left}px`,
              visibility: tipStyle.visibility,
            }}
          >
            <span class="ion-broken-chip__tooltip-title">Unresolved reference</span>
            {t.context && (
              <span class="ion-broken-chip__tooltip-row">
                Cited as: <strong>{t.context}</strong>
              </span>
            )}
            {t.id && (
              <span class="ion-broken-chip__tooltip-row">
                Marker id: <code>{t.id}</code>
              </span>
            )}
            <span class="ion-broken-chip__tooltip-reason">{reasonText}</span>
          </span>,
          document.body,
        )}
    </span>
  )
}
